import { ChevronDown, type LucideIcon } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';

import { Button } from '~/components/ui/button';
import { cn } from '~/utils/cn';

interface CollapsibleSectionCardProps {
  icon: LucideIcon;
  title: string;
  /** What the header shows on the right, open or closed (e.g. a total). */
  summary: ReactNode;
  /** Root id; the toggle, summary and body derive `${testId}-…`. */
  testId: string;
  defaultOpen?: boolean;
  children: ReactNode;
}

/**
 * The Batch Info sheet's section card with a header that opens and closes the body: the title and a
 * one-line summary stay visible, the detail renders only when open. The header is a real button
 * (`aria-expanded`), so it works from the keyboard and reads correctly to screen readers.
 */
export function CollapsibleSectionCard({
  icon: Icon,
  title,
  summary,
  testId,
  defaultOpen = false,
  children,
}: CollapsibleSectionCardProps) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();
  return (
    <section data-testid={testId} className="bg-card rounded-xl border">
      <h3 className="m-0">
        <Button
          type="button"
          variant="ghost"
          aria-expanded={open}
          {...(open ? { 'aria-controls': bodyId } : {})}
          data-testid={`${testId}-toggle`}
          onClick={() => {
            setOpen((v) => !v);
          }}
          className={cn(
            'hover:bg-muted/40 h-auto w-full cursor-pointer justify-start gap-2 rounded-xl px-4 py-3 text-left',
            open && 'rounded-b-none',
          )}
        >
          <Icon aria-hidden="true" className="text-muted-foreground h-4 w-4 shrink-0" />
          <span className="text-foreground text-sm font-semibold">{title}</span>
          <span
            data-testid={`${testId}-summary`}
            className="text-muted-foreground ml-auto min-w-0 truncate text-xs font-normal"
          >
            {summary}
          </span>
          <ChevronDown
            aria-hidden="true"
            className={cn(
              'text-muted-foreground h-4 w-4 shrink-0 transition-transform',
              open && 'rotate-180',
            )}
          />
        </Button>
      </h3>
      {open && (
        <div id={bodyId} data-testid={`${testId}-body`} className="border-t p-4">
          {children}
        </div>
      )}
    </section>
  );
}
